import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Badge, Button, Input, Modal, Toggle } from '@/components/ui';
import {
  updateProbationPolicy,
  type ProbationPolicy,
  type ProbationPolicyInput,
} from '@/api/services/probation.service';
import { getErrorMessage } from '@/lib/getErrorMessage';
import { describeReminderDays, parseReminderDays } from './probationFormat';

type NumberField =
  | 'durationDays'
  | 'escalateAfterDaysOverdue'
  | 'maxExtensions'
  | 'maxExtensionDays';

const NUMBER_FIELDS: Record<NumberField, { label: string; min: number; hint: string }> = {
  durationDays: {
    label: 'Probation length (days)',
    min: 1,
    hint: 'Counted from the joining date.',
  },
  maxExtensions: {
    label: 'Maximum extensions',
    min: 0,
    hint: 'How many times one probation can be extended. 0 turns extensions off.',
  },
  maxExtensionDays: {
    label: 'Longest extension (days)',
    min: 1,
    hint: 'Each extension adds up to this many days to the current end date.',
  },
  escalateAfterDaysOverdue: {
    label: 'Escalate after (days overdue)',
    min: 0,
    hint: 'Organization admins are told when a decision is this late.',
  },
};

const GROUP_HEADING = 'text-sm font-semibold text-gray-900 dark:text-gray-100';

/** Company probation policy form. Mounted only while open, so it starts from the saved policy. */
export function ProbationPolicyDialog({
  companyId,
  policy,
  onClose,
}: {
  companyId: string;
  policy: ProbationPolicy;
  onClose: () => void;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const [numbers, setNumbers] = useState<Record<NumberField, string>>(() => ({
    durationDays: String(policy.durationDays),
    escalateAfterDaysOverdue: String(policy.escalateAfterDaysOverdue),
    maxExtensions: String(policy.maxExtensions),
    maxExtensionDays: String(policy.maxExtensionDays),
  }));
  const [reminders, setReminders] = useState(policy.reminderDaysBefore.join(', '));
  const [reviewRequired, setReviewRequired] = useState(policy.reviewRequired);
  const [formError, setFormError] = useState<string | null>(null);

  const fieldErrors: Partial<Record<NumberField, string>> = {};
  for (const [key, { label, min }] of Object.entries(NUMBER_FIELDS) as Array<
    [NumberField, (typeof NUMBER_FIELDS)[NumberField]]
  >) {
    const raw = numbers[key].trim();
    if (!/^\d+$/.test(raw) || Number(raw) < min) {
      fieldErrors[key] =
        `${label.replace(/ \(.*\)$/, '')} must be a whole number of at least ${min}.`;
    }
  }
  const extensionsOff = numbers.maxExtensions.trim() === '0';
  const reminderDays = parseReminderDays(reminders);
  const reminderError =
    reminderDays === null ? 'Use whole days above zero, separated by commas.' : undefined;
  const valid = Object.keys(fieldErrors).length === 0 && reminderDays !== null;

  const save = useMutation({
    mutationFn: (input: ProbationPolicyInput) => updateProbationPolicy(companyId, input),
    onSuccess: () => {
      toast.success('Probation rules saved. New probations will use them.');
      void queryClient.invalidateQueries({ queryKey: ['hr-probation-policy', companyId] });
      onClose();
    },
    onError: (mutationError) => {
      const message = getErrorMessage(mutationError);
      setFormError(message);
      toast.error(message);
    },
  });

  const handleSubmit = (): void => {
    if (!valid || reminderDays === null) return;
    setFormError(null);
    save.mutate({
      durationDays: Number(numbers.durationDays),
      reminderDaysBefore: reminderDays,
      escalateAfterDaysOverdue: Number(numbers.escalateAfterDaysOverdue),
      maxExtensions: Number(numbers.maxExtensions),
      maxExtensionDays: Number(numbers.maxExtensionDays),
      reviewRequired,
    });
  };

  const numberInput = (key: NumberField, disabled = false): React.ReactElement => {
    const { label, min, hint } = NUMBER_FIELDS[key];
    return (
      <Input
        id={`probation-policy-${key}`}
        label={label}
        type="number"
        inputMode="numeric"
        min={min}
        step={1}
        disabled={disabled}
        className={disabled ? 'opacity-60' : undefined}
        value={numbers[key]}
        error={fieldErrors[key]}
        hint={fieldErrors[key] ? undefined : disabled ? 'Extensions are off.' : hint}
        onChange={(event) => setNumbers((current) => ({ ...current, [key]: event.target.value }))}
      />
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Probation rules"
      className="max-w-lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="probation-policy-form"
            disabled={!valid || save.isPending}
            loading={save.isPending}
          >
            Save rules
          </Button>
        </>
      }
    >
      <form
        id="probation-policy-form"
        className="space-y-6"
        onSubmit={(event) => {
          event.preventDefault();
          handleSubmit();
        }}
      >
        <div className="space-y-2">
          <Badge variant={policy.isDefault ? 'neutral' : 'info'} size="sm">
            {policy.isDefault ? 'Standard rules' : 'Custom rules'}
          </Badge>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            New probations use these rules from the day they open. Probations already running keep
            the rules they started with.
          </p>
        </div>

        <fieldset className="space-y-4">
          <legend className={GROUP_HEADING}>Length</legend>
          {numberInput('durationDays')}
        </fieldset>

        <fieldset className="space-y-4">
          <legend className={GROUP_HEADING}>Extensions</legend>
          <div className="grid gap-4 sm:grid-cols-2">
            {numberInput('maxExtensions')}
            {numberInput('maxExtensionDays', extensionsOff)}
          </div>
        </fieldset>

        <fieldset className="space-y-4">
          <legend className={GROUP_HEADING}>Reminders and escalation</legend>
          <Input
            id="probation-policy-reminders"
            label="Reminder days before end"
            placeholder="14, 7, 1"
            value={reminders}
            error={reminderError}
            hint={
              reminderError
                ? undefined
                : `${describeReminderDays(reminderDays ?? [])}. Separate days with commas; leave empty for none.`
            }
            onChange={(event) => setReminders(event.target.value)}
          />
          {numberInput('escalateAfterDaysOverdue')}
        </fieldset>

        <fieldset className="border-t border-gray-100 pt-5 dark:border-gray-700">
          <legend className="sr-only">Review</legend>
          <Toggle
            id="probation-policy-review"
            checked={reviewRequired}
            onChange={setReviewRequired}
            label="Require a manager review before confirmation"
            description="A review has to be recorded before anyone can confirm the person."
            align="end"
          />
        </fieldset>

        {formError && (
          <p className="text-sm text-danger" role="alert">
            {formError}
          </p>
        )}
      </form>
    </Modal>
  );
}
